import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  IconButton, TablePagination, alpha, CircularProgress, Alert, Menu, MenuItem, TextField, FormControl, InputLabel, Select,
  useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, Globe } from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import ExportTableButton from '../components/common/ExportTableButton';

const CountriesView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countries, setCountries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    country_name: '',
    country_status: 'Activated'
  });

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionCountry, setActionCountry] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/admin/countries', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch
        }
      });
      setCountries(response.data.countries || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleMenuOpen = (event, country) => {
    setAnchorEl(event.currentTarget);
    setActionCountry(country);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionCountry(null);
  };

  const handleOpenModal = (country = null) => {
    if (country) {
      setEditingCountry(country);
      setFormData({
        country_name: country.country_name,
        country_status: country.country_status || 'Activated'
      });
    } else {
      setEditingCountry(null);
      setFormData({
        country_name: '',
        country_status: 'Activated'
      });
    }
    setError(null);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (actionCountry) {
      try {
        setLoading(true);
        await axios.delete(`/admin/countries/${actionCountry._id}`);
        setDeleteDialogOpen(false);
        setActionCountry(null);
        loadCountries();
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to delete country');
        setDeleteDialogOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (editingCountry) {
        await axios.put(`/admin/countries/${editingCountry._id}`, formData);
      } else {
        await axios.post('/admin/countries', formData);
      }
      setIsModalOpen(false);
      loadCountries();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save country');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Countries"
        subtitle="Manage country level geographical boundaries and coverage"
        count={total}
        countLabel="countries"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => handleOpenModal()}
          >
            New Country
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Toolbar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search country..."
          hasActiveFilters={Boolean(searchTerm)}
          onResetFilters={() => { setSearchTerm(''); setPage(0); }}
        >
          <ExportTableButton
            data={countries}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/admin/countries', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                },
              });
              return res.data.countries || [];
            }}
            columns={[
              { label: 'Country Name', key: 'country_name' },
              { label: 'Status', key: 'country_status' },
            ]}
            filename="countries"
          />
        </FilterBar>

        {error && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ backgroundColor: 'background.neutral' }}>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Country Name</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {countries.length > 0 ? countries.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{c.country_name}</TableCell>
                  <TableCell>
                    <StatusBadge
                      status={c.country_status === 'Activated' ? 'active' : 'inactive'}
                      label={c.country_status || 'Activated'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, c)}>
                      <MoreVertical size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : !loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ p: 0 }}>
                    <EmptyState
                      icon={Globe}
                      title="No countries found"
                      description="Try adjusting your search query or add a new country."
                      actionLabel="Add Country"
                      onAction={() => handleOpenModal()}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: { sx: { width: 140, p: 0.5 } }
        }}
      >
        <MenuItem onClick={() => handleOpenModal(actionCountry)} sx={{ fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal Dialog */}
      <AdminDialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm">
        <form onSubmit={handleFormSubmit}>
          <AdminDialogHeader
            title={editingCountry ? 'Edit Country' : 'New Country'}
            subtitle="Configure country details and regional market status"
            icon={Globe}
            onClose={() => setIsModalOpen(false)}
          />
          <AdminDialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
              )}
              <TextField
                label="Country Name"
                fullWidth
                required
                value={formData.country_name}
                onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                placeholder="e.g. India, United States, United Arab Emirates"
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.country_status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, country_status: e.target.value })}
                >
                  <MenuItem value="Activated">Activated</MenuItem>
                  <MenuItem value="Deactivated">Deactivated</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </AdminDialogContent>
          <AdminDialogActions>
            <Button onClick={() => setIsModalOpen(false)} color="inherit" disabled={loading} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={16} color="inherit" />}
              sx={{ px: 3, fontWeight: 700 }}
            >
              {loading ? 'Saving...' : 'Save Country'}
            </Button>
          </AdminDialogActions>
        </form>
      </AdminDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        itemName={actionCountry?.country_name}
        itemType="Country"
      />
    </Box>
  );
};

export default CountriesView;
